import * as React from 'react';
import { Theme, Typography, IconButton, List, ListItem, useTheme } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import {
	OrderProduct,
	Shopper,
	Languages,
	OrderProductWithUnits,
	CurrencyObject,
} from 'common/types';
import { RiCheckboxCircleFill, RiDeleteBinFill } from 'react-icons/ri';
import classNames from 'classnames';
import {
	getVariantOrderProductsWithUnitsFromOrderProductList,
	getProductNameWithVariant,
} from 'common/utils/productUtils';
import { getPricingString } from 'common/utils/common';
import { getTranslation } from 'common/modules/translations';
import { useTranslation } from 'services/localization/useTranslation';

interface OwnProps {
	shopper: Shopper;
	rentalProducts: OrderProduct[];
	productsToBeConfirmed?: OrderProduct[];
	activeShopperId: string;
	index: number;
	lang: Languages;
	currency: CurrencyObject;
	changeActiveShopperId: (shopperId: string) => void;
	deleteShopper?: (e: React.MouseEvent<HTMLElement>, shopperId: string) => void;
}

type Props = OwnProps & WithStyles<typeof styles>;

export const ShopperCardShoppingCart = (props: Props) => {
	const {
		classes,
		rentalProducts,
		shopper,
		activeShopperId,
		changeActiveShopperId,
		deleteShopper,
		index,
		lang,
		productsToBeConfirmed,
		currency,
	} = props;
	const { t } = useTranslation();
	const theme = useTheme();
	const selected = shopper.id === activeShopperId;
	const shopperProducts = rentalProducts.filter((p) => p.shopperId === shopper.id);
	let shopperProductsWithUnits: OrderProductWithUnits[] = [];
	if (productsToBeConfirmed) {
		const shopperProductsToBeConfirmed = productsToBeConfirmed.filter(
			(p) => p.shopperId === shopper.id,
		);
		const shopperProductsWithUnitsConfirmed = getVariantOrderProductsWithUnitsFromOrderProductList(
			shopperProducts,
		);
		const shopperProductsWithUnitsUnConfirmed = getVariantOrderProductsWithUnitsFromOrderProductList(
			shopperProductsToBeConfirmed,
			true,
		);
		shopperProductsWithUnits = [
			...shopperProductsWithUnitsConfirmed,
			...shopperProductsWithUnitsUnConfirmed,
		];
	} else {
		shopperProductsWithUnits = getVariantOrderProductsWithUnitsFromOrderProductList(
			shopperProducts,
		);
	}

	return (
		<div
			key={shopper.id}
			className={classNames(classes.shopperCard, selected ? classes.selectedCard : undefined)}
			onClick={() => changeActiveShopperId(shopper.id)}
		>
			{deleteShopper && (
				<IconButton
					className={classes.shopperDelete}
					aria-label={t('common:actions.delete')}
					onClick={(e) => deleteShopper(e, shopper.id)}
					size="large"
				>
					<RiDeleteBinFill />
				</IconButton>
			)}
			<div className={classes.cardContent}>
				<div className={classes.cardHeader}>
					{selected ? (
						<IconButton aria-label="Selected" className={classes.selectedIcon} size="large">
							<RiCheckboxCircleFill color={theme.palette.primary.main} />
						</IconButton>
					) : null}
					<Typography variant="h6">
						{shopper.firstName || t('common:person.person') + ' ' + (index + 1)}
					</Typography>
				</div>
				<List className={classes.shopperProductList}>
					{shopperProductsWithUnits.map((product, idx) => {
						const { units, pricing, variant } = product;
						const productName = getProductNameWithVariant(
							getTranslation(product.name, lang),
							variant || null,
							lang,
							product,
						);
						return (
							<ListItem
								key={`${product.id}_${idx}`}
								className={classNames(
									classes.productItem,
									product.unConfirmed
										? classes.primaryColor
										: productsToBeConfirmed
										? classes.greyColor
										: undefined,
								)}
							>
								<Typography variant="body2">
									{units} x {productName}
								</Typography>
								<div className={classes.priceWrapper}>
									<Typography variant="body2">
										<b>{getPricingString(pricing.listPrice, currency)}</b>
									</Typography>
									{!!pricing.deposit ? (
										<Typography variant="caption">
											{t('shoppingCart.deposit', 'Deposit:')}{' '}
											{getPricingString(pricing.deposit, currency)}
										</Typography>
									) : null}
								</div>
							</ListItem>
						);
					})}
				</List>
			</div>
		</div>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		shopperCard: {
			position: 'relative',
			cursor: 'pointer',
			width: '100%',
			borderBottom: `1px solid ${theme.palette.divider}`,
			'&:hover': {
				backgroundColor: theme.palette.action.hover,
			},
		},
		cardContent: {
			padding: 16,
		},
		selectedCard: {
			backgroundColor: theme.palette.action.selected,
		},
		shopperDelete: {
			position: 'absolute',
			top: 8,
			right: 8,
			padding: 8,
		},
		cardHeader: {
			display: 'flex',
			alignItems: 'center',
			marginBottom: 8,
		},
		selectedIcon: {
			padding: 0,
			marginRight: 8,
		},
		shopperProductList: {
			padding: 0,
		},
		productItem: {
			justifyContent: 'space-between',
			alignItems: 'flex-start',
			padding: '2px 0',
		},
		primaryColor: {
			color: theme.palette.primary.main,
		},
		greyColor: {
			color: theme.palette.text.secondary,
		},
		priceWrapper: {
			flex: '0 0 auto',
			textAlign: 'right',
		},
	});

export default withStyles(styles)(ShopperCardShoppingCart);
